<script setup>
import { useDialogPluginComponent } from 'quasar'
import { reactive, ref, toRefs } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { regexPlateNumber } from '../../../utils'
import apiCustomer from '../../../api/customer.api'
import apiAddress from '../../../api/address.api'
import useCustomer from '../../../store/customer.store'
import otpToNumber from '../../../api/otpToNumber.api'

const { dialogRef, onDialogOK, onDialogCancel } = useDialogPluginComponent()
const { getCustomerList, filterOptions } = useCustomer()
const $q = useQuasar()
const { t } = useI18n()

const props = defineProps({
  id: String
})

const otp = ref('')
const otpDialog = ref(false)
const otpLoading = ref(false)
const otpId = ref('')

const { id } = toRefs(props)
const loading = ref(false)
const confirmDialog = ref(false)
const carNumberInput = ref('')
const carNumbers = ref([])
const addresses = ref([])
const permitLoading = reactive({})
const pendingApartment = reactive({})
const data = reactive({
  name: '',
  phone: '',
  email: '',
})

function addCarNumber() {
  const val = carNumberInput.value.toUpperCase().trim()
  if (!val) return
  // if (!regexPlateNumber.test(val)) {
  //   $q.notify({ message: t('customer.invalid_plate'), type: 'negative' })
  //   return
  // }
  if (carNumbers.value.includes(val)) return
  carNumbers.value.push(val)
  carNumberInput.value = ''
}

function removeCarNumber(index) {
  carNumbers.value.splice(index, 1)
}

let currentData = {}

function showSubmit() {
  confirmDialog.value = true
}

const getOtp = async () => {
  if (data.phone === currentData.phone) {
    submit()
    return
  }
  showOtpDialog()
  otpLoading.value = true
  try {
    otpId.value = (await otpToNumber.create({ phoneNumber: data.phone })).data.id
    $q.notify({ message: t('otp.sent'), type: 'positive' })
    otp.value = ''
  } catch (error) {
    $q.notify({ message: error?.data?.error, type: 'negative' })
  } finally {
    otpLoading.value = false
  }
}

async function submit() {
  try {
    loading.value = true
    await apiCustomer.update({
      id: id.value,
      name: data.name,
      phone: data.phone,
      email: data.email,
      carNumbers: carNumbers.value,
      code: otp.value,
      otpId: otpId.value,
    })
    $q.notify({ message: t('customer.updated'), type: 'positive' })
    getCustomerList(filterOptions.value)
    onDialogOK()
    otpDialog.value = false
  } catch (error) {
    $q.notify({ message: error?.data?.error, type: 'negative' })
  } finally {
    loading.value = false
  }
}

// ── Permit helpers ────────────────────────────────────────────────────────────

function permitKey(address, deviceIdOrOId) {
  return `${String(address._id)}-${String(deviceIdOrOId)}`
}

function getCustomerDevices(address) {
  return address.customers?.[0]?.devices ?? []
}

function getUnregisteredDevices(address) {
  const registered = getCustomerDevices(address)
  return (address.devices ?? []).filter(
    d => !registered.find(cd => String(cd.deviceOId) === String(d._id))
  )
}

function getDeviceName(address, deviceOId) {
  return address.devices?.find(d => String(d._id) === String(deviceOId))?.name ?? String(deviceOId)
}

async function togglePermit(address, cd) {
  const key = permitKey(address, cd.deviceOId)
  if (permitLoading[key]) return
  permitLoading[key] = true
  try {
    await apiAddress.addPermit({
      addressId: String(address._id), phone: data.phone, devices: [{
        deviceOId: String(cd.deviceOId),
        apartment: cd.apartment ?? '',
        permitted: !cd.permitted,
      }]
    })
    cd.permitted = !cd.permitted
  } catch (e) {
    $q.notify({ message: e?.data?.error ?? String(e), type: 'negative' })
  } finally {
    permitLoading[key] = false
  }
}

async function removePermitDevice(address, cd) {
  const key = permitKey(address, cd.deviceOId)
  if (permitLoading[key]) return
  permitLoading[key] = true
  try {
    await apiAddress.removePermit({addressId: String(address._id), phone: data.phone, devices: [{
      deviceOId: String(cd.deviceOId),
      apartment: cd.apartment ?? '',
      permitted: false,
    }]})
    const devices = address.customers[0].devices
    const idx = devices.findIndex(d => String(d.deviceOId) === String(cd.deviceOId))
    if (idx !== -1) devices.splice(idx, 1)
  } catch (e) {
    $q.notify({ message: e?.data?.error ?? String(e), type: 'negative' })
  } finally {
    permitLoading[key] = false
  }
}

function showApartmentInput(address, device) {
  const key = permitKey(address, device._id)
  pendingApartment[key] = { showing: true, value: '' }
}

async function addPermit(address, device) {
  const key = permitKey(address, device._id)
  const entry = pendingApartment[key]
  if (permitLoading[key] || !entry) return
  permitLoading[key] = true
  try {
    const apartment = entry.value?.trim() ?? ''
    await apiAddress.addPermit({addressId: String(address._id), phone: data.phone, devices: [{
      deviceOId: String(device._id),
      apartment,
      permitted: true,
    }]})
    if (!address.customers[0]) {
      address.customers.push({ phone: data.phone, devices: [] })
    }
    address.customers[0].devices.push({ deviceOId: device._id, apartment, permitted: true })
    entry.showing = false
    entry.value = ''
  } catch (e) {
    $q.notify({ message: e?.data?.error ?? String(e), type: 'negative' })
  } finally {
    permitLoading[key] = false
  }
}

// ── Init / fetch ──────────────────────────────────────────────────────────────

function init(customer, addressList) {
  data.name = customer.name ?? ''
  data.phone = customer.phone ?? ''
  data.email = customer.email ?? ''
  carNumbers.value = [...(customer.carNumbers ?? [])]
  currentData = { ...customer }
  addresses.value = addressList ?? []
}

async function fetch() {
  loading.value = true
  try {
    const { data: res } = await apiCustomer.getOne(id.value)
    init(res.customer, res.addresses)
  } finally {
    loading.value = false
  }
}

const showOtpDialog = () => {
  otpDialog.value = true
}

fetch()
</script>

<template lang='pug'>
.customer-update
  q-dialog(ref='dialogRef' persistent)
    q-card.overflow-hidden(style='width: 720px; max-width: 95vw;')
      q-form(@submit='getOtp')
        q-card-section
          .text-h6 {{ t('customer.update_title') }}
        q-separator
        q-card-section.scroll(style='max-height: 70vh; min-height: 40vh;')
          q-input.q-ma-xs.q-mt-sm(
            stack-label
            outlined
            v-model='data.name'
            :label="t('customer.name')"
            no-error-icon
            hide-bottom-space
            hide-hint
          )
          q-input.q-ma-xs.q-mt-sm(
            stack-label
            outlined
            v-model='data.phone'
            :label="t('customer.phone')"
            no-error-icon
            hide-bottom-space
            hide-hint
          )
          q-input.q-ma-xs.q-mt-sm(
            stack-label
            outlined
            v-model='data.email'
            :label="t('customer.email')"
            no-error-icon
            hide-bottom-space
            hide-hint
          )
          .q-ma-xs.q-mt-sm
            .row.no-wrap.items-start
              q-input(
                style='flex: 1'
                stack-label
                outlined
                :modelValue='carNumberInput'
                @update:modelValue='val => carNumberInput = val.toUpperCase()'
                :label="t('customer.car_numbers')"
                no-error-icon
                hide-bottom-space
                hide-hint
                @keydown.enter.prevent='addCarNumber'
              )
              q-btn.q-ml-xs.q-mt-auto(
                unelevated
                color='primary'
                icon='add'
                @click='addCarNumber'
                style='height: 56px'
              )
            .row.wrap.q-mt-xs(v-if='carNumbers.length')
              q-chip(
                v-for='(num, i) in carNumbers'
                :key='num'
                removable
                @remove='removeCarNumber(i)'
                color='grey-3'
                text-color='dark'
              ) {{ num }}
          div.q-mx-xs.q-mt-md(v-if='addresses.length')
            q-separator.q-mb-sm
            div.q-mb-sm(v-for='address in addresses' :key='String(address._id)')
              .text-caption.text-weight-bold.text-grey-6.q-mb-xs {{ address.name }}
              .row.q-col-gutter-sm
                .col-6
                  transition-group.row.wrap(name='chip' tag='div' style='gap: 4px')
                    q-chip(
                      v-for='cd in getCustomerDevices(address)'
                      :key='String(cd.deviceOId)'
                      dense
                      :color="cd.permitted ? 'green-6' : 'grey-4'"
                      :text-color="cd.permitted ? 'white' : 'dark'"
                      style='margin: 2px'
                    )
                      q-checkbox(
                        dense
                        size='xs'
                        color='dark'
                        keep-color
                        :model-value='cd.permitted'
                        :disable='!!permitLoading[permitKey(address, cd.deviceOId)]'
                        @update:model-value='togglePermit(address, cd)'
                      )
                      span.q-mx-xs {{ getDeviceName(address, cd.deviceOId) }}
                      span.text-caption.q-ml-xs(v-if='cd.apartment') · {{ cd.apartment }}
                      q-icon(
                        name='close'
                        size='xs'
                        style='cursor: pointer; margin-left: 4px'
                        @click.stop='removePermitDevice(address, cd)'
                      )
                .col-6
                  transition-group.row.wrap(name='chip' tag='div' style='gap: 4px')
                    div(
                      v-for='device in getUnregisteredDevices(address)'
                      :key='String(device._id)'
                      :style="pendingApartment[permitKey(address, device._id)]?.showing ? 'width: 100%' : 'display: inline-flex'"
                    )
                      transition(name='chip-swap' mode='out-in')
                        div.row.no-wrap.items-center.q-mb-xs(v-if="pendingApartment[permitKey(address, device._id)]?.showing" key='input')
                          q-input(
                            dense
                            outlined
                            v-model="pendingApartment[permitKey(address, device._id)].value"
                            :label='device.name'
                            :placeholder="t('addresses.permit_dialog.apartmentNumber', { number: '#' })"
                            style='flex: 1'
                            hide-bottom-space
                            @keydown.enter.prevent='addPermit(address, device)'
                          )
                          q-btn(flat dense icon='check' color='primary' size='xs' :loading='!!permitLoading[permitKey(address, device._id)]' @click='addPermit(address, device)')
                          q-btn(flat dense icon='close' color='grey' size='xs' @click="pendingApartment[permitKey(address, device._id)].showing = false")
                        q-chip(
                          v-else
                          key='chip'
                          dense
                          clickable
                          color='grey-2'
                          text-color='dark'
                          style='margin: 2px'
                          @click='showApartmentInput(address, device)'
                        )
                          q-icon(name='add' size='xs' color='primary')
                          span.q-ml-xs {{ device.name }}
        q-separator
        q-card-actions(align='right')
          q-btn(flat unelevated color='grey' @click='onDialogCancel' :label="t('actions.cancel')")
          q-btn(flat type='submit' unelevated color='primary' :label="t('actions.save')")
      q-inner-loading(:showing='loading')
  q-dialog(v-model='otpDialog')
    q-card.overflow-hidden(style='width: 300px; max-width: 70vw;')
      q-inner-loading(:showing='otpLoading')
      q-form(@submit='showSubmit')
        q-card-section
          .text-h6 {{ t('otp.title') }}
        q-separator
        q-card-section
          q-input.q-ma-xs.q-mt-sm(
            v-model='otp'
            label='OTP'
            mask='####'
            no-error-icon
            hide-bottom-space
            hide-hint
            :disable='otpLoading'
          )
        q-separator
        q-card-actions(align='right')
          q-btn(flat unelevated color='grey' @click='otpDialog = false' :label="t('actions.cancel')")
          q-btn(flat type='submit' unelevated color='primary' :disable='otpLoading' :label="t('otp.confirm')")
  q-dialog(v-model='confirmDialog' persistent)
    q-card
      q-card-section
        q-icon(size='lg' name='priority_high' color='yellow')
        span {{ ' ' + t('customer.confirm_update_text') }}
      q-card-actions(align='right')
        q-btn(flat unelevated color='grey' :label="t('actions.no')" v-close-popup)
        q-btn(flat unelevated color='primary' :label="t('actions.yes')" v-close-popup @click='submit')
</template>

<style scoped>
.chip-enter-active,
.chip-leave-active {
  transition: all 0.2s ease;
}

.chip-enter-from,
.chip-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

.chip-leave-active {
  position: absolute;
}

.chip-swap-enter-active,
.chip-swap-leave-active {
  transition: all 0.15s ease;
}

.chip-swap-enter-from,
.chip-swap-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>